import { useEffect, useState } from "react";
import { Search, History, FileDown, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";
import { utils, writeFile } from "xlsx";
import { useApp } from "@/context/AppContext";
import { api, errMsg } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { inputCls } from "@/components/ui-kit";
import { usePdfReport } from "@/components/PdfReport";

const ACTIONS = ["shop.plan", "shop.trial", "shop.suspend", "shop.unsuspend", "shop.delete", "request.approve", "request.reject",
  "user.disable", "user.enable", "user.password", "admin.create", "admin.update", "admin.password", "admin.delete", "settings.update"];
const TONE = { delete: "#D93838", suspend: "#E67E22", disable: "#E67E22", reject: "#E67E22", password: "#7C5CC4" };
const tone = (a) => TONE[a.split(".")[1]] || "#0B2A5B";
const ymd = (d) => d.toISOString().slice(0, 10);

export default function ActivityTab() {
  const { t, lang } = useApp();
  const pdf = usePdfReport();
  const [q, setQ] = useState("");
  const [action, setAction] = useState("");
  const [from, setFrom] = useState(() => ymd(new Date(Date.now() - 30 * 864e5)));
  const [to, setTo] = useState(() => ymd(new Date()));
  const [list, setList] = useState([]);
  const params = { q, action, date_from: from, date_to: to };
  useEffect(() => {
    const id = setTimeout(() => api.get("/admin/logs", { params }).then((r) => setList(r.data)).catch((e) => toast.error(errMsg(e))), 300);
    return () => clearTimeout(id);
  }, [q, action, from, to]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchAll = async () => (await api.get("/admin/logs", { params: { ...params, limit: 5000 } })).data;
  const toRows = (logs) => logs.map((l) => ({
    date: fmtDate(l.at, lang), admin: `${l.admin_name} (${l.admin_email})`, action: t(`log_${l.action}`), target: l.target || "", detail: l.detail || "",
  }));
  const cols = [["date", t("log_date")], ["admin", t("tab_admins")], ["action", t("log_action")], ["target", t("log_target")], ["detail", t("log_detail")]];
  const name = `orbit-activity-${from}_${to}`;
  const exportPdf = async () => {
    try {
      const rows = toRows(await fetchAll());
      pdf.exportReport({ filename: `${name}.pdf`, brand: `${t("app_name")} · ${t("admin_title")}`, title: t("tab_activity"),
        subtitle: `${from} → ${to} · ${rows.length}`, columns: cols.map(([key, label]) => ({ key, label })), rows });
    } catch (e) { toast.error(errMsg(e)); }
  };
  const exportXlsx = async () => {
    try {
      const rows = toRows(await fetchAll()).map((r) => Object.fromEntries(cols.map(([k, label]) => [label, r[k]])));
      const ws = utils.json_to_sheet(rows);
      ws["!cols"] = [{ wch: 20 }, { wch: 36 }, { wch: 28 }, { wch: 30 }, { wch: 30 }];
      const wb = utils.book_new();
      utils.book_append_sheet(wb, ws, "Activity");
      writeFile(wb, `${name}.xlsx`);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const btn = "h-12 px-4 rounded-xl font-bold text-sm flex items-center justify-center gap-2 disabled:opacity-50";
  return (
    <div className="space-y-3" data-testid="admin-activity-tab">
      <div className="grid sm:grid-cols-2 lg:grid-cols-[1fr_220px_160px_160px] gap-2">
        <div className="relative">
          <Search className="w-5 h-5 absolute start-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("log_search")} className={`${inputCls} ps-10`} data-testid="admin-logs-search" />
        </div>
        <select value={action} onChange={(e) => setAction(e.target.value)} className={inputCls} data-testid="admin-logs-action-filter">
          <option value="">{t("log_all_actions")}</option>
          {ACTIONS.map((a) => <option key={a} value={a}>{t(`log_${a}`)}</option>)}
        </select>
        <input type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)} aria-label={t("log_from")} className={inputCls} data-testid="admin-logs-from" />
        <input type="date" value={to} min={from} onChange={(e) => setTo(e.target.value)} aria-label={t("log_to")} className={inputCls} data-testid="admin-logs-to" />
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm text-muted-foreground me-auto" data-testid="admin-logs-count">{list.length} · {from} → {to}</span>
        <button type="button" onClick={exportPdf} disabled={pdf.busy} className={`${btn} bg-[#0B2A5B] text-white`} data-testid="admin-logs-export-pdf"><FileDown className="w-5 h-5" />PDF</button>
        <button type="button" onClick={exportXlsx} className={`${btn} bg-[#1F9D55] text-white`} data-testid="admin-logs-export-xlsx"><FileSpreadsheet className="w-5 h-5" />Excel</button>
      </div>
      {!list.length && <div className="rounded-xl bg-white border border-border p-8 text-center text-muted-foreground" data-testid="admin-logs-empty"><History className="w-8 h-8 mx-auto mb-2" />{t("log_empty")}</div>}
      <ol className="space-y-2" data-testid="admin-logs-list">
        {list.map((l) => (
          <li key={l.id} className="rounded-xl bg-white border border-border p-3 flex flex-wrap items-center gap-x-3 gap-y-1 card-soft" data-testid="admin-log-row">
            <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: tone(l.action) }} />
            <span className="font-bold" data-testid="admin-log-action">{t(`log_${l.action}`)}</span>
            {l.target && <span className="font-semibold text-[#0B2A5B]" dir="auto">{l.target}</span>}
            {l.detail && <span className="text-sm text-muted-foreground" dir="ltr">{l.detail}</span>}
            <span className="ms-auto text-sm text-muted-foreground whitespace-nowrap" data-testid="admin-log-meta">{l.admin_name} · {fmtDate(l.at, lang)}</span>
          </li>
        ))}
      </ol>
      {pdf.node}
    </div>
  );
}
